import { getIssuer, validToken } from "../helpers/jwt";
import { getItem, removeItem, setItem } from "../helpers/localStorage";
import { setRedirect, splitPullRequestPath } from "../pullRequestRedirect";
import { AUTH_CONFIGS, AuthConfig } from "./authConfigs";
import {
    forgetDeviceSignOut,
    refreshCognitoIdentity,
} from "./cognitoIdentityWrapper";
import { fetchAccountUser } from "./dinteroAuthApi";
import {
    clearCognitoSession,
    getCognitoSession,
    setCognitoSession,
} from "./localStorage";
import { AuthenticatedUser, CognitoSession } from "./types";

const sessionFromLocation = async (): Promise<CognitoSession | undefined> => {
    // Only used with federated login (google, vy)

    // Read and remove all tokens and codes from the current url
    const code = popAuthorizationCodeFromQuery();
    const { access_token, id_token } = popImplicitGrantParamsFromHash();

    if (code) {
        // Authorization code flow, code in query param

        // NOTE:
        // id token is used when authenticating user with
        // external cognito as the id token includes the email
        // of the user
        const config = getFederatedConfig();
        const { id_token, refresh_token } =
            await fetchTokensFromAuthorizationCode(config, code);
        return {
            cognito_access_token: id_token,
            cognito_refresh_token: refresh_token,
            cognito_config: config,
        };
    }

    if (access_token || id_token) {
        // LEGACY Implicit grant flow
        // On a successful login AWS Cognito redirects users to the backoffice with the access_token in the window.location.hash
        // Eg. https://example.com/#access_token=<jwt>&id_token=<jwt>&expires_in=1234
        let cognito_access_token = access_token;
        const config = resolveTokenAuthConfig(id_token || undefined);

        if (config.flow === "oauth" && id_token) {
            // id_token is used when authenticating user with external
            // cognito via oauth as the id token includes the email of
            // the user
            cognito_access_token = id_token;
        }

        if (cognito_access_token) {
            const session = {
                cognito_access_token,
                cognito_config: config,
            };
            return session;
        }
    }
};

const popAuthorizationCodeFromQuery = () => {
    // Read and remove access_code for oauth flow from url query
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    if (code) {
        url.searchParams.delete("code");
        window.history.replaceState({}, document.title, url.toString());
    }
    return code;
};

const popImplicitGrantParamsFromHash = () => {
    // Read and remove params used for legacy implicit grant flow from url hash
    const hash = window.location.hash;
    const prefix = "#";
    const hashParamsString = hash.substring(prefix.length);
    const hashParams = new URLSearchParams(hashParamsString);
    const access_token = hashParams.get("access_token");
    const id_token = hashParams.get("id_token");
    const expires_in = hashParams.get("expires_in");
    window.location.hash = "";
    return {
        access_token,
        id_token,
        expires_in,
    };
};

const fetchTokensFromAuthorizationCode = async (
    config: AuthConfig | undefined,
    code: string,
) => {
    if (!config || !config.client_id || !config.domain) {
        throw new Error("Bad AuthConfig for oauth authentication");
    }

    const response = await fetch(`https://${config.domain}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
            grant_type: "authorization_code",
            client_id: config.client_id,
            code: code || "",
            redirect_uri: window.location.origin,
        }),
    });
    if (!response.ok) {
        throw new Error("Unable to use oauth access code");
    }
    const data = (await response.json()) as {
        id_token: string;
        auth_token: string;
        refresh_token?: string;
        expires_in: string;
    };

    if (config.issuer && getIssuer(data.id_token) !== config.issuer) {
        // Ensure issuer in token matches config for sanity
        throw new Error("Token has unexpected issuer");
    }
    return data;
};

export const fetchTokensFromRefreshToken = async (
    config: AuthConfig | undefined,
    refreshToken: string,
) => {
    if (!config || !config.client_id || !config.domain) {
        throw new Error("Bad AuhtConfig for oauth authentication");
    }
    const response = await fetch(`https://${config.domain}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: config.client_id,
            refresh_token: refreshToken,
        }),
    });

    if (!response.ok) {
        throw new Error("Unable to use oauth refresh token");
    }
    const data = (await response.json()) as {
        id_token: string;
        auth_token: string;
        refresh_token?: string;
        expires_in: string;
    };
    if (config.issuer && getIssuer(data.id_token) !== config.issuer) {
        // Ensure issuer in token matches config for sanity
        throw new Error("Token has unexpected issuer");
    }

    // for aws cognito a new refresh token is not returned when it is used
    return {
        ...data,
        refresh_token: data.refresh_token || refreshToken,
    };
};

const getFederatedConfig = () => {
    // If VY config is defined use vy config, otherwise use default federated login config
    if (AUTH_CONFIGS.aws_cognito_sso_enterprise_vy) {
        return AUTH_CONFIGS.aws_cognito_sso_enterprise_vy;
    }
    return AUTH_CONFIGS.aws_cognito_federated_login_google;
};

export const loginViaRedirect = () => {
    // used for VY and google
    clearCognitoSession();
    rememberCurrentLocation();
    const config = getFederatedConfig();
    window.location.assign(config.urls.login);
};

const redirectToLogout = (authConfig: AuthConfig) => {
    window.location.assign(authConfig.urls.logout);
};

const resolveTokenAuthConfig = (token?: string): AuthConfig => {
    // Inspect issuer of token to get the correct config
    const authConfigFromIssuer = Object.values(AUTH_CONFIGS).find((config) => {
        if (!token || !config?.issuer) {
            return false;
        }
        return getIssuer(token) === config?.issuer;
    });
    if (authConfigFromIssuer) {
        return authConfigFromIssuer;
    }

    if (AUTH_CONFIGS.aws_cognito_sso_enterprise_vy) {
        // If VY config is defined we use VY config
        return AUTH_CONFIGS.aws_cognito_sso_enterprise_vy;
    }

    // Default config if we cannot identify issuer and VY config is undefined
    return AUTH_CONFIGS.aws_cognito_conventional_login;
};

export const validCognitoSession = async (
    session: CognitoSession | undefined,
) => {
    try {
        if (!session) {
            return;
        }
        if (validToken(session.cognito_access_token)) {
            return session;
        }
        return await refresh(session);
    } catch {
        return;
    }
};

type InitializeAuthFunction = (
    prevSession: CognitoSession | undefined,
) => Promise<AuthenticatedUser | undefined>;

export const authenticate: InitializeAuthFunction = async (
    prevSession?: CognitoSession,
) => {
    try {
        const session =
            (await validCognitoSession(prevSession)) ||
            (await sessionFromLocation());
        if (session) {
            const user = await fetchAccountUser(session.cognito_access_token);
            if (user) {
                setCognitoSession(session);
                handleRedirectIfPresent();
                return {
                    ...session,
                    account_user: user,
                };
            }
        }
    } catch (e: unknown) {
        console.error(e);
    }
};

export const refresh = async (
    cognitoSession: CognitoSession,
): Promise<CognitoSession | undefined> => {
    // for VY and federated login we do ouath refresh
    try {
        if (
            cognitoSession.cognito_refresh_token &&
            cognitoSession.cognito_config?.flow === "oauth" &&
            cognitoSession.cognito_config?.domain &&
            cognitoSession.cognito_config?.client_id
        ) {
            const result = await fetchTokensFromRefreshToken(
                cognitoSession.cognito_config,
                cognitoSession.cognito_refresh_token,
            );
            // The oauth token response uses its own field names; map them onto
            // the cognito session. The id token is used as the access token
            // (it carries the user email), mirroring the login flow.
            return {
                ...cognitoSession,
                cognito_access_token: result.id_token,
                cognito_refresh_token: result.refresh_token,
            };
        }
        if (
            cognitoSession.cognito_refresh_token &&
            cognitoSession.cognito_config?.flow === "sdk"
        ) {
            const result = await refreshCognitoIdentity(
                cognitoSession.cognito_refresh_token,
            );
            return result;
        }
    } catch (e) {
        // ignore errors
        console.error(e);
    }
};

export const logout = async (forgetDevice: boolean) => {
    rememberCurrentLocation();
    const cognitoSession = clearCognitoSession();
    const authConfig =
        cognitoSession?.cognito_config ||
        resolveTokenAuthConfig(cognitoSession?.cognito_access_token);
    try {
        if (forgetDevice) {
            await forgetDeviceSignOut();
        }
    } catch (e) {
        console.error(e);
    }
    redirectToLogout(authConfig);
};

export const setRedirects = (pathname: string, search: string) => {
    setItem("pathname_redirect", pathname);
    setItem("pathname_search", search);
};

/**
 * Cognito only ever redirects back to the bare origin (its registered
 * redirect_uri / logout_uri), so a federated signin or a logout always lands on
 * "/" -- on a pull request build that means the master bundle instead of
 * /_pr/<sha>/. Remember the full pathname (base path included) before leaving so
 * handleRedirectIfPresent() can send the user back once we return to "/".
 *
 * Skipped when we are already on "/": there is nothing to restore, and it would
 * overwrite a more specific redirect stored earlier (see InvalidSession).
 */
const rememberCurrentLocation = () => {
    if (window.location.pathname !== "/") {
        setRedirects(window.location.pathname, window.location.search);
    }
};

const removeAllRedirects = () => {
    removeItem("pathname_redirect");
    removeItem("pathname_search");
};

/**
 * Go back to the pull request build a remembered location points into, if any.
 * Cloudfront only serves that bundle at its base path, so navigating to the deep
 * url would load master's index.html and flash the master app before
 * RedirectToPullRequestRoute bounces back. Park the rest of the path and go
 * straight to the base path instead. Returns true when navigating.
 */
const redirectToPullRequestBuild = (pathname: string, search: string) => {
    const target = splitPullRequestPath(pathname);
    if (!target) {
        return false;
    }
    if (target.path !== "/") {
        setRedirect(target.path, search);
    }
    window.location.href = target.basePath;
    return true;
};

/**
 * Ensure that we can deep-link back to the original page after federated signin (or expired session). Necessary for Shopify callback to work.
 */
export const handleRedirectIfPresent = () => {
    const pathname = getItem("pathname_redirect") || "";
    const search = getItem("pathname_search") || "";
    if (window.location.pathname !== "/" || !(pathname || search)) {
        return;
    }
    removeAllRedirects();

    if (redirectToPullRequestBuild(pathname, search)) {
        return;
    }

    window.location.href = `${pathname}${search}`;
};

/**
 * The signed out counterpart of handleRedirectIfPresent, for when we are about
 * to show the login screen. Cognito signs out to the bare origin just like it
 * signs in there, so a signout started inside a pull request build drops the
 * user on master's login screen. Hand the login over to the pull request build
 * instead -- QA'ing a pull request includes QA'ing its login page.
 *
 * Only pull request locations are followed: there is nothing to gain from deep
 * linking a signed out user, and consuming the redirect here would lose it for
 * handleRedirectIfPresent once they are signed in. Returns true when navigating,
 * in which case the caller must not render.
 */
export const handleLoginRedirectIfPresent = () => {
    const pathname = getItem("pathname_redirect") || "";
    const search = getItem("pathname_search") || "";
    if (window.location.pathname !== "/" || !splitPullRequestPath(pathname)) {
        return false;
    }
    removeAllRedirects();
    return redirectToPullRequestBuild(pathname, search);
};

export const isFederatedSignin = (session?: CognitoSession) => {
    const sessionObject = session ?? getCognitoSession();
    return (
        getIssuer(sessionObject?.cognito_access_token) ===
        AUTH_CONFIGS.aws_cognito_federated_login_google.issuer
    );
};
